import { useState, useEffect, useRef, useCallback } from 'react';
import { CapacitorUpdater } from '@capgo/capacitor-updater';
import { SplashScreen } from './components/screens/SplashScreen';
import { OnboardingScreen } from './components/screens/OnboardingScreen';
import { LoginScreen } from './components/screens/LoginScreen';
import { auth } from './config/firebase.config';
import { onAuthStateChanged } from 'firebase/auth';
import { dbService } from './services/firebase-db.service';
import { getRiderShareRate } from './utils/pricingSettings';
import { SignUpScreen } from './components/screens/SignUpScreen';
import { ApplicationReviewScreen } from './components/screens/ApplicationReviewScreen';
import { TrainingScreen } from './components/screens/TrainingScreen';
import { HomeScreen } from './components/screens/HomeScreen';
import { JobDetailScreen } from './components/screens/JobDetailScreen';
import { ActiveJobScreen } from './components/screens/ActiveJobScreen';
import { PickupConfirmationScreen } from './components/screens/PickupConfirmationScreen';
import { DeliveryConfirmationScreen } from './components/screens/DeliveryConfirmationScreen';
import { EarningsScreen } from './components/screens/EarningsScreen';
import { ShiftManagementScreen } from './components/screens/ShiftManagementScreen';
import { ProfileScreen } from './components/screens/ProfileScreen';
import { EditProfileScreen } from './components/screens/EditProfileScreen';
import { SettingsScreen } from './components/screens/SettingsScreen';
import { IncidentReportScreen } from './components/screens/IncidentReportScreen';
import { HistoryScreen } from './components/screens/HistoryScreen';
import { DesignSystemScreen } from './components/screens/DesignSystemScreen';
import { PermissionsScreen } from './components/screens/PermissionsScreen';
import { FleetTypeSelectionScreen } from './components/screens/FleetTypeSelectionScreen';
import { PerformanceDashboardScreen } from './components/screens/PerformanceDashboardScreen';
import { HelpCenterScreen } from './components/screens/HelpCenterScreen';
import { PromotionsScreen } from './components/screens/PromotionsScreen';
import { LeaderboardScreen } from './components/screens/LeaderboardScreen';
import { AdvancedSettingsScreen } from './components/screens/AdvancedSettingsScreen';
import { VehicleManagementScreen } from './components/screens/VehicleManagementScreen';
import { GearComplianceScreen } from './components/screens/GearComplianceScreen';
import NotificationScreen from './components/screens/NotificationScreen';
import { CustomerChat } from './components/chat/CustomerChat';
import { SOSButton } from './components/safety/SOSButton';
import { integrationService } from './services/integration.service';
import { demoService } from './services/demo.service';
import { notificationService } from './services/notification.service';
import { nativePushService } from './services/native-push.service';
import { stopNativeNavigation } from './services/nativeNavigation';
import {
  persistActiveDelivery,
  clearActiveDelivery,
  getPersistedActiveJobId,
  getPersistedJobStatus,
  setAutoStartNavigation,
  consumeAutoStartNavigation,
  jobStatusFromOrderStatus,
  screenForJobStep,
  resolveJobStep,
  markPickupConfirmed,
  isPickupConfirmed,
  clearPickupConfirmed,
} from './services/activeDeliverySession';
import { isValidJobCoords } from './utils/jobCoordinates';
import { storageService } from './services/firebase-storage.service';
import { dataUrlToFile } from './utils/dataUrlToFile';
import { compressDataUrl } from './utils/compressImage';
import { useRiderLocationSync } from './hooks/useRiderLocationSync';
import { ConnectionStatus } from './components/ui/ConnectionStatus';
import { AppUpdateBanner } from './components/ui/AppUpdateBanner';
import { useAppUpdateCheck } from './hooks/useAppUpdateCheck';
import { isCapacitorNative } from './utils/nativePlatform';
import { walletService } from './services/wallet.service';
import { chatService } from './services/chat.service';
import { runScreenBackHandler } from './services/appBackHandler';

export type Screen = 
  | 'splash'
  | 'onboarding'
  | 'login'
  | 'signup'
  | 'application-review'
  | 'training'
  | 'permissions'
  | 'home'
  | 'job-detail'
  | 'navigation'
  | 'pickup-confirmation'
  | 'delivery-confirmation'
  | 'earnings'
  | 'shift-management'
  | 'profile'
  | 'edit-profile'
  | 'settings'
  | 'incident-report'
  | 'history'
  | 'design-system'
  | 'fleet-type-selection'
  | 'notifications'
  | 'chat'
  | 'performance'
  | 'help-center'
  | 'promotions'
  | 'leaderboard'
  | 'advanced-settings'
  | 'vehicle-management'
  | 'gear-compliance'
  | 'sos-demo';

const PARENT_SCREEN: Partial<Record<Screen, Screen>> = {
  onboarding: 'splash',
  login: 'onboarding',
  signup: 'login',
  permissions: 'login',
  'application-review': 'login',
  training: 'home',
  'job-detail': 'home',
  navigation: 'home',
  'pickup-confirmation': 'navigation',
  'delivery-confirmation': 'navigation',
  chat: 'navigation',
  earnings: 'home',
  'shift-management': 'home',
  profile: 'home',
  'edit-profile': 'profile',
  settings: 'profile',
  'incident-report': 'home',
  history: 'home',
  notifications: 'home',
  'fleet-type-selection': 'profile',
  performance: 'home',
  'help-center': 'home',
  promotions: 'home',
  leaderboard: 'home',
  'advanced-settings': 'settings',
  'vehicle-management': 'profile',
  'gear-compliance': 'profile',
  'design-system': 'home',
  'sos-demo': 'home',
};

function syncAppHistory(screen: Screen, mode: 'push' | 'replace' = 'push') {
  const state = { go7Screen: screen };
  try {
    if (mode === 'replace') {
      window.history.replaceState(state, '', window.location.href);
    } else {
      window.history.pushState(state, '', window.location.href);
    }
  } catch (_) {}
}

export default function App() {
  // Tell Capgo the app loaded successfully — prevents auto-rollback on live updates
  useEffect(() => {
    CapacitorUpdater.notifyAppReady();
  }, []);

  // Sync launch/standard fares from go7 admin (pricingSettings/current)
  useEffect(() => {
    const unsub = dbService.initPricingSync();
    return unsub;
  }, []);

  const [currentScreen, setCurrentScreen] = useState<Screen>('splash');
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);
  const [selectedJob, setSelectedJob] = useState<any>(null);
  const [currentCustomer, setCurrentCustomer] = useState<{ name: string; phone: string }>({ name: 'Customer', phone: '' });
  const [jobStatus, setJobStatus] = useState<'going-to-pickup' | 'at-pickup' | 'going-to-delivery' | 'at-delivery'>('going-to-pickup');
  const [riderFleetType, setRiderFleetType] = useState<'rider' | 'bicycle' | 'leg-fleet'>('rider');
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [riderName, setRiderName] = useState('Rider');
  const [userId, setUserId] = useState<string>('');
  const [isInSignupFlow, setIsInSignupFlow] = useState(false); // Track if user is completing signup
  const [resumeSignup, setResumeSignup] = useState(false); // Returning user who started signup earlier
  const [navAutoStart, setNavAutoStart] = useState(false);
  const [activeDeliveryJobId, setActiveDeliveryJobId] = useState<string | null>(null);
  const [isAcceptingJob, setIsAcceptingJob] = useState(false);
  const initialAuthHandledRef = useRef(false);
  const currentScreenRef = useRef<Screen>('splash');
  const activeDeliveryJobIdRef = useRef<string | null>(null);
  const selectedJobIdRef = useRef<string | null>(null);
  const {
    updateAvailable,
    remoteVersion,
    applyUpdate,
  } = useAppUpdateCheck();
  const [dismissedUpdateBanner, setDismissedUpdateBanner] = useState(false);

  useEffect(() => {
    currentScreenRef.current = currentScreen;
  }, [currentScreen]);

  useEffect(() => {
    // Keep visual viewport locked (iOS address bar / keyboard shrink)
    const lockViewport = () => {
      const h = window.visualViewport?.height || window.innerHeight;
      document.documentElement.style.setProperty('--app-height', `${h}px`);
    };
    lockViewport();
    window.visualViewport?.addEventListener('resize', lockViewport);
    window.addEventListener('resize', lockViewport);
    return () => {
      window.visualViewport?.removeEventListener('resize', lockViewport);
      window.removeEventListener('resize', lockViewport);
    };
  }, []);

  useEffect(() => {
    activeDeliveryJobIdRef.current = activeDeliveryJobId;
  }, [activeDeliveryJobId]);

  useEffect(() => {
    selectedJobIdRef.current = selectedJobId;
  }, [selectedJobId]);

  const screensWithOwnGpsSync = ['navigation', 'pickup-confirmation', 'delivery-confirmation'];
  const syncLocationInBackground =
    Boolean(activeDeliveryJobId) && !screensWithOwnGpsSync.includes(currentScreen);
  useRiderLocationSync(
    activeDeliveryJobId || undefined,
    userId || undefined,
    syncLocationInBackground
  );
  
  // Notification state - Real-time from notification service
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState<Array<{
    id: string;
    type: 'job' | 'earnings' | 'system' | 'alert';
    title: string;
    message: string;
    time: string;
    read: boolean;
    jobId?: string;
    amount?: string;
  }>>([]);
  
  // Chat state
  const [showChat, setShowChat] = useState(false);

  const fleetTypeToVehicle = (fleet: typeof riderFleetType): 'bike' | 'bicycle' | 'leg' =>
    fleet === 'rider' ? 'bike' : fleet === 'leg-fleet' ? 'leg' : 'bicycle';

  const restoreActiveDelivery = useCallback(
    async (jobId: string, riderUid: string, fleet: typeof riderFleetType) => {
      const vehicleType = fleetTypeToVehicle(fleet);
      let job = await dbService.getJobById(jobId, vehicleType, riderUid);
      if (!job) return null;

      const customer = await dbService.getJobCustomerInfo(jobId);
      if (customer) setCurrentCustomer(customer);

      const contacts = await dbService.getJobContacts(jobId);
      if (contacts) {
        job = { ...job, ...contacts };
      }

      setSelectedJobId(jobId);
      setSelectedJob(job);
      setActiveDeliveryJobId(jobId);

      const rawStatus = job.firestoreStatus || job.status;
      const fromServer = rawStatus
        ? jobStatusFromOrderStatus(String(rawStatus))
        : 'going-to-pickup';
      const status = resolveJobStep(fromServer, getPersistedJobStatus());
      setJobStatus(status);
      persistActiveDelivery(jobId, status);
      if (status === 'going-to-delivery' || status === 'at-delivery') {
        markPickupConfirmed(jobId);
      }
      return status;
    },
    []
  );

  // Check for existing auth session on app load
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      console.log('🔐 Auth state changed:', user?.email);
      
      if (user) {
        // Store user ID for wallet operations
        setUserId(user.uid);
        
        // User is logged in, check verification status
        console.log('✅ User already logged in, checking verification status...');
        
        try {
          const profile = await dbService.getRiderProfile(user.uid);
          
          if (!profile) {
            const signupInProgress =
              isInSignupFlow ||
              sessionStorage.getItem('go7-signup-in-progress') === '1';

            if (signupInProgress) {
              console.log('📝 No profile yet — user is in signup flow, staying on signup');
              setCurrentScreen('signup');
            } else {
              console.log('⚠️ No profile found — resume signup');
              setResumeSignup(true);
              setIsInSignupFlow(true);
              setCurrentScreen('signup');
            }
          } else {
            // Store rider name for chat
            if (profile.name) {
              setRiderName(profile.name);
            }
            
            // Load rider's fleet type from profile
            if (profile.fleetType) {
              setRiderFleetType(profile.fleetType);
            }
            
            if (profile.verificationStatus === 'pending') {
              // Profile exists but not approved yet
              console.log('⏳ Rider verification pending');
              setCurrentScreen('application-review');
            } else if (profile.verificationStatus === 'rejected') {
              // Profile rejected
              console.log('❌ Rider verification rejected');
              setCurrentScreen('application-review');
            } else if (profile.verificationStatus === 'approved') {
              console.log('✅ Rider verified and approved');
              try {
                await dbService.clearStaleRiderData(user.uid);
              } catch (e) {
                console.warn('Stale job cleanup:', e);
              }
              const approvalKey = `go7-approval-notified-${user.uid}`;
              const wasJustApproved = !localStorage.getItem(approvalKey);
              if (wasJustApproved) {
                localStorage.setItem(approvalKey, '1');
                notificationService.trigger(
                  'system',
                  '🎉 Application Approved!',
                  'Welcome to Go7! You can now go online and start accepting deliveries.',
                );
              }

              const fleet = profile.fleetType || 'rider';
              const activeJobId = profile.currentJobId || profile.currentOrder || null;
              const persistedJobId = getPersistedActiveJobId();

              if (!activeJobId) {
                clearActiveDelivery();
                setActiveDeliveryJobId(null);
              } else if (persistedJobId && persistedJobId !== activeJobId) {
                clearActiveDelivery();
              }

              if (activeJobId) {
                const restoredStep = await restoreActiveDelivery(activeJobId, user.uid, fleet);
                if (restoredStep) {
                  setActiveDeliveryJobId(activeJobId);
                  if (!initialAuthHandledRef.current) {
                    const screen = screenForJobStep(restoredStep);
                    currentScreenRef.current = screen;
                    setCurrentScreen(screen);
                    syncAppHistory(screen, 'replace');
                    setNavAutoStart(consumeAutoStartNavigation());
                  }
                  await notificationService.ensurePushSetup();
              void nativePushService.ensureSetup();
                  initialAuthHandledRef.current = true;
                  setIsCheckingAuth(false);
                  return;
                }
              }

              if (!initialAuthHandledRef.current) {
                currentScreenRef.current = 'home';
                setCurrentScreen('home');
                syncAppHistory('home', 'replace');
              }
              setActiveDeliveryJobId(null);
              clearActiveDelivery();

              // Initialize notifications + request push permission for approved riders
              await notificationService.ensurePushSetup();
              void nativePushService.ensureSetup();
              initialAuthHandledRef.current = true;
            } else {
              // Any other status (undefined, etc) should go to application review
              console.log('⏳ Unknown verification status, showing application review');
              setCurrentScreen('application-review');
            }
          }
        } catch (error) {
          console.error('Error checking profile:', error);
          setCurrentScreen('home');
        }
      } else {
        // Not logged in, show normal flow
        console.log('❌ No active session, showing onboarding');
      }
      
      setIsCheckingAuth(false);
    });

    return () => unsubscribe();
  }, [restoreActiveDelivery]);

  // Live approval: when admin approves, rider leaves review screen immediately
  useEffect(() => {
    if (!userId) return;
    if (currentScreen !== 'application-review') return;

    const unsubscribe = dbService.subscribeToRiderProfile(userId, (profile) => {
      if (profile?.verificationStatus === 'approved') {
        void notificationService.ensurePushSetup();
        void nativePushService.ensureSetup();
        setCurrentScreen('training');
      }
    });

    return () => unsubscribe();
  }, [userId, currentScreen]);

  // Live assignment: a job assigned while the app is already open surfaces immediately
  const lastAssignedJobIdRef = useRef<string | null>(null);
  const riderFleetTypeRef = useRef(riderFleetType);
  useEffect(() => {
    riderFleetTypeRef.current = riderFleetType;
  }, [riderFleetType]);

  useEffect(() => {
    if (!userId) return;

    const unsubscribe = dbService.subscribeToRiderProfile(userId, (profile) => {
      const assignedJobId = profile?.currentJobId || profile?.currentOrder || null;
      if (assignedJobId === lastAssignedJobIdRef.current) return;
      lastAssignedJobIdRef.current = assignedJobId;
      if (!assignedJobId) return;
      if (assignedJobId === activeDeliveryJobIdRef.current) return;
      if (activeDeliveryJobIdRef.current) return; // don't hijack an in-progress delivery

      void (async () => {
        const step = await restoreActiveDelivery(assignedJobId, userId, riderFleetTypeRef.current);
        if (!step) return;
        notificationService.trigger(
          'new-job',
          'New Job Assigned',
          'A delivery has been assigned to you.',
          { jobId: assignedJobId }
        );
        const screen = screenForJobStep(step);
        currentScreenRef.current = screen;
        setCurrentScreen(screen);
        syncAppHistory(screen);
      })();
    });

    return () => unsubscribe();
  }, [userId, restoreActiveDelivery]);

  // Subscribe to real-time notifications
  useEffect(() => {
    if (currentScreen === 'home' || currentScreen === 'navigation') {
      // Load notification history
      const history = notificationService.getNotificationHistory();
      const mappedHistory = history.map(n => ({
        id: n.id,
        type: (n.type === 'new-job' ? 'job' : 
               n.type === 'earnings-update' ? 'earnings' : 
               n.type === 'message' ? 'system' : 
               'system') as 'job' | 'earnings' | 'system' | 'alert',
        title: n.title,
        message: n.body,
        time: formatNotificationTime(n.timestamp),
        read: n.read,
        jobId: n.data?.jobId,
        amount: n.data?.amount
      }));
      setNotifications(mappedHistory);

      // Subscribe to new notifications
      const unsubscribe = notificationService.subscribe((notification) => {
        const mapped = {
          id: notification.id,
          type: (notification.type === 'new-job' ? 'job' : 
                 notification.type === 'earnings-update' ? 'earnings' : 
                 notification.type === 'message' ? 'system' : 
                 'system') as 'job' | 'earnings' | 'system' | 'alert',
          title: notification.title,
          message: notification.body,
          time: formatNotificationTime(notification.timestamp),
          read: notification.read,
          jobId: notification.data?.jobId,
          amount: notification.data?.amount
        };
        setNotifications(prev => [mapped, ...prev]);
      });

      return () => unsubscribe();
    }
  }, [currentScreen]);

  // Helper function to format notification time
  const formatNotificationTime = (date: Date) => {
    const now = new Date();
    const diffMs = now.getTime() - new Date(date).getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return 'Earlier';
  };

  const handleAppBack = useCallback(() => {
    if (runScreenBackHandler()) return;

    const screen = currentScreenRef.current;
    const parent = PARENT_SCREEN[screen];
    if (!parent) return;

    if (parent === 'navigation') {
      const jobId = activeDeliveryJobIdRef.current || selectedJobIdRef.current;
      void navigateRef.current(parent, jobId || undefined);
      return;
    }

    currentScreenRef.current = parent;
    setCurrentScreen(parent);
    syncAppHistory(parent);
  }, []);

  useEffect(() => {
    if (!window.history.state?.go7Screen) {
      syncAppHistory(currentScreenRef.current, 'replace');
    }

    const onPopState = () => {
      handleAppBack();
      window.setTimeout(() => syncAppHistory(currentScreenRef.current), 0);
    };

    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [handleAppBack]);

  useEffect(() => {
    if (!isCapacitorNative()) return;

    let listener: { remove: () => Promise<void> } | undefined;
    import('@capacitor/app')
      .then(({ App }) => App.addListener('backButton', () => handleAppBack()))
      .then((sub) => {
        listener = sub;
      })
      .catch(() => {});

    return () => {
      listener?.remove().catch(() => {});
    };
  }, [handleAppBack]);

  const navigateRef = useRef<(screen: Screen, jobId?: string) => Promise<void>>(async () => {});

  const navigate = async (screen: Screen, jobId?: string) => {
    if (jobId) {
      setSelectedJobId(jobId);
      try {
        const vehicleType: 'bike' | 'bicycle' | 'leg' = 
          riderFleetType === 'rider' ? 'bike' : 
          riderFleetType === 'leg-fleet' ? 'leg' : 
          'bicycle';

        let job = await dbService.getJobById(jobId, vehicleType, userId);
        if (!job) {
          const jobs = await dbService.getAvailableJobs(100, vehicleType);
          job = jobs.find(j => j.id === jobId) || null;
        }

        if (job) {
          if (!job.customerPhone || !job.pickupPhone) {
            const contacts = await dbService.getJobContacts(jobId);
            if (contacts) {
              job = { ...job, ...contacts };
            }
          }
          const loadedJob = job;
          setSelectedJob((prev: any) => {
            if (!prev || prev.id !== jobId) return loadedJob;
            return {
              ...loadedJob,
              pickupLocation: isValidJobCoords(loadedJob.pickupLocation)
                ? loadedJob.pickupLocation
                : prev.pickupLocation,
              deliveryLocation: isValidJobCoords(loadedJob.deliveryLocation)
                ? loadedJob.deliveryLocation
                : prev.deliveryLocation,
            };
          });
          setCurrentCustomer({
            name: loadedJob.customerName || 'Customer',
            phone: loadedJob.customerPhone || '',
          });
          console.log('✅ Loaded job with real coordinates:', loadedJob);
        } else {
          console.error('❌ Job not found:', jobId);
          alert('This delivery is no longer available. It may have already been accepted.');
          if (['job-detail', 'navigation', 'pickup-confirmation', 'delivery-confirmation', 'chat'].includes(screen)) {
            setCurrentScreen('home');
            syncAppHistory('home');
            return;
          }
        }
      } catch (error) {
        console.error('❌ Failed to fetch job:', error);
      }
    }
    setCurrentScreen(screen);
    if (screen !== 'splash') {
      syncAppHistory(screen);
    }
  };

  navigateRef.current = navigate;

  // Keep active delivery in session so riders can resume after refresh or accidental back
  useEffect(() => {
    if (!selectedJob?.id) return;
    if (
      currentScreen === 'navigation' ||
      currentScreen === 'pickup-confirmation' ||
      currentScreen === 'delivery-confirmation'
    ) {
      persistActiveDelivery(selectedJob.id, jobStatus);
      setActiveDeliveryJobId(selectedJob.id);
    }
  }, [selectedJob?.id, jobStatus, currentScreen]);

  useEffect(() => {
    if (!navAutoStart || currentScreen !== 'navigation') return;
    const timer = window.setTimeout(() => setNavAutoStart(false), 1500);
    return () => window.clearTimeout(timer);
  }, [navAutoStart, currentScreen]);

  // Real job data only — no mock fallbacks for production testing
  const currentJob = selectedJob ? {
    id: selectedJob.id,
    pickupName: selectedJob.pickupName || selectedJob.pickupAddress.split(',')[0] || 'Pickup',
    pickupAddress: selectedJob.pickupAddress,
    pickupPhone: selectedJob.pickupPhone || '',
    pickupLocation: selectedJob.pickupLocation,
    pickupLandmark: selectedJob.pickupLandmark,
    deliveryName: selectedJob.deliveryAddress.split(',')[0] || 'Delivery',
    deliveryAddress: selectedJob.deliveryAddress,
    deliveryLocation: selectedJob.deliveryLocation,
    deliveryLandmark: selectedJob.deliveryLandmark,
    distance: selectedJob.distance,
    estimatedEarnings: `₦${selectedJob.earnings}`,
    estimatedTime: selectedJob.estimatedTime,
    customerName: selectedJob.customerName || currentCustomer.name,
    customerPhone: selectedJob.customerPhone || currentCustomer.phone,
    requiresPin: selectedJob.requiresPin,
    status: jobStatus,
    packageSize: 'Medium',
    isCOD: selectedJob.isCOD ?? false,
    codAmount: selectedJob.codAmount ? `₦${selectedJob.codAmount}` : '₦0',
    type: 'parcel' as const,
    fleetType: riderFleetType,
    vehicleType: selectedJob.vehicleType,
    deliveryFee: selectedJob.deliveryFee,
    packageType: selectedJob.packageType,
    notes: selectedJob.notes,
  } : null;

  const JOB_FLOW_SCREENS: Screen[] = [
    'job-detail',
    'navigation',
    'pickup-confirmation',
    'delivery-confirmation',
    'chat',
  ];

  useEffect(() => {
    if (JOB_FLOW_SCREENS.includes(currentScreen) && !selectedJob) {
      setCurrentScreen('home');
      syncAppHistory('home');
    }
  }, [currentScreen, selectedJob]);

  // Initialize integration service on login
  useEffect(() => {
    // TODO: Implement these integration service methods when backend is ready
    // Listen for new messages from WebSocket
    // const unsubscribeMessages = integrationService.onNewMessage((message) => {
    //   console.log('New message received:', message);
    // });

    // Listen for notifications from WebSocket
    // const unsubscribeNotifications = integrationService.onNotification((notification) => {
    //   console.log('New notification received:', notification);
    //   setNotifications(prev => [notification, ...prev]);
    // });

    // Listen for job updates
    // const unsubscribeJobs = integrationService.onJobUpdate((job) => {
    //   console.log('Job update received:', job);
    //   // Handle job updates
    // });

    // return () => {
    //   unsubscribeMessages();
    //   unsubscribeNotifications();
    //   unsubscribeJobs();
    // };
  }, []);

  return (
    <div className="app-shell" style={{ height: 'var(--app-height, 100dvh)' }}>
      <div className="app-shell-frame bg-white">
        {/* Connection Status */}
        <ConnectionStatus />
        {updateAvailable && !dismissedUpdateBanner && (
          <AppUpdateBanner
            remoteVersion={remoteVersion}
            onUpdate={applyUpdate}
            onDismiss={() => setDismissedUpdateBanner(true)}
          />
        )}
        
        {/* Screen Content — shell never scrolls; screens manage their own panes */}
        <div className="absolute inset-0 overflow-hidden">
          {currentScreen === 'splash' && (
            <SplashScreen 
              onComplete={() => navigate('onboarding')}
              onShowMapShowcase={() => navigate('design-system')}
              onShowAnimationsShowcase={() => navigate('design-system')}
              onShowNotificationsShowcase={() => navigate('design-system')}
            />
          )}
          
          {currentScreen === 'onboarding' && (
            <OnboardingScreen 
              onComplete={() => navigate('login')}
              onSkip={() => navigate('login')}
            />
          )}
          
          {currentScreen === 'login' && (
            <LoginScreen 
              onSuccess={() => navigate('permissions')} 
              onNavigateToSignUp={() => {
                sessionStorage.setItem('go7-signup-in-progress', '1');
                setIsInSignupFlow(true);
                setResumeSignup(false);
                navigate('signup');
              }}
              onIncompleteProfile={() => {
                sessionStorage.setItem('go7-signup-in-progress', '1');
                setIsInSignupFlow(true);
                setResumeSignup(true);
                navigate('signup');
              }}
            />
          )}
          
          {currentScreen === 'signup' && (
            <SignUpScreen 
              resumeSignup={resumeSignup}
              onSuccess={() => {
                sessionStorage.removeItem('go7-signup-in-progress');
                setIsInSignupFlow(false);
                setResumeSignup(false);
                navigate('permissions');
              }} 
              onBackToLogin={() => {
                sessionStorage.removeItem('go7-signup-in-progress');
                setIsInSignupFlow(false);
                setResumeSignup(false);
                navigate('login');
              }}
            />
          )}
          
          {currentScreen === 'permissions' && (
            <PermissionsScreen onComplete={() => navigate('application-review')} />
          )}
          
          {currentScreen === 'home' && (
            <HomeScreen 
              onJobSelect={(jobId, cached) => {
                if (cached) {
                  setSelectedJob((prev: any) => ({
                    ...(prev?.id === jobId ? prev : {}),
                    id: jobId,
                    pickupLocation: cached.pickupLocation,
                    deliveryLocation: cached.deliveryLocation,
                    pickupAddress: cached.pickupAddress,
                    deliveryAddress: cached.deliveryAddress,
                    pickupName: cached.pickupName,
                    customerName: cached.customerName,
                    customerPhone: cached.customerPhone,
                    pickupPhone: cached.pickupPhone,
                  }));
                }
                navigate('job-detail', jobId);
              }}
              activeJobId={activeDeliveryJobId}
              onResumeActiveJob={async () => {
                const jobId = activeDeliveryJobId || getPersistedActiveJobId();
                if (!jobId) return;
                const step = await restoreActiveDelivery(jobId, userId, riderFleetType);
                if (!step) return;
                const screen = screenForJobStep(step);
                if (screen === 'delivery-confirmation') {
                  navigate('delivery-confirmation', jobId);
                } else {
                  navigate('navigation', jobId);
                }
              }}
              onNavigateToEarnings={() => navigate('earnings')}
              onNavigateToProfile={() => navigate('profile')}
              onNavigateToShift={() => navigate('shift-management')}
              onNavigateToHistory={() => navigate('history')}
              onNavigateToNotifications={() => navigate('notifications')}
              riderFleetType={riderFleetType}
              unreadNotifications={notifications.filter(n => !n.read).length}
            />
          )}
          
          {currentScreen === 'job-detail' && currentJob && (
            <JobDetailScreen
              job={currentJob}
              onBack={() => navigate('home')}
              onAccept={async () => {
                if (isAcceptingJob) return;
                if (!selectedJob || selectedJob.id !== currentJob.id) {
                  alert('Could not load this delivery. Please go back and refresh available jobs.');
                  return;
                }
                if (
                  activeDeliveryJobId &&
                  activeDeliveryJobId !== currentJob.id
                ) {
                  alert(
                    'You already have an active delivery. Tap "Active delivery in progress" to resume it before accepting another job.'
                  );
                  return;
                }
                const hasPickupCoords = isValidJobCoords(currentJob.pickupLocation);
                const hasDeliveryCoords = isValidJobCoords(currentJob.deliveryLocation);
                if (!hasPickupCoords || !hasDeliveryCoords) {
                  alert(
                    'This order is missing pickup or delivery map coordinates. Navigation cannot start — wait for a corrected order or contact support.'
                  );
                  return;
                }
                setIsAcceptingJob(true);
                try {
                  await dbService.acceptJob(currentJob.id, userId);
                  await dbService.markEnRouteToPickup(currentJob.id, userId);
                  console.log('✅ Job accepted successfully');
                  const contacts = await dbService.getJobContacts(currentJob.id);
                  let customerName = currentCustomer.name;
                  let customerPhone = currentCustomer.phone;
                  if (contacts) {
                    setSelectedJob((prev: any) => (prev ? { ...prev, ...contacts } : prev));
                    customerName = contacts.customerName;
                    customerPhone = contacts.customerPhone;
                    setCurrentCustomer({ name: customerName, phone: customerPhone });
                  } else {
                    const customer = await dbService.getJobCustomerInfo(currentJob.id);
                    if (customer) {
                      customerName = customer.name;
                      customerPhone = customer.phone;
                      setCurrentCustomer(customer);
                    }
                  }
                  notificationService.trigger(
                    'new-job',
                    '✅ Job Accepted!',
                    `Head to ${currentJob.pickupName} for pickup. ${currentJob.estimatedTime} away.`,
                    { jobId: currentJob.id }
                  );
                  clearPickupConfirmed(currentJob.id);
                  setJobStatus('going-to-pickup');
                  persistActiveDelivery(currentJob.id, 'going-to-pickup');
                  setActiveDeliveryJobId(currentJob.id);
                  setNavAutoStart(true);
                  setAutoStartNavigation(true);
                  await chatService.initializeChat(
                    currentJob.id,
                    { id: userId, name: riderName, phone: '' },
                    { name: customerName, phone: customerPhone }
                  );
                  navigate('navigation', currentJob.id);
                } catch (error) {
                  console.error('❌ Failed to accept job:', error);
                  if (error instanceof Error && error.message === 'Distance exceeds walking limit') {
                    alert('⚠️ This delivery is too far for walking.\n\nDistance exceeded: This job is over 1.5km. As a foot worker, you can only accept jobs within 1.5km walking distance.');
                  } else if (error instanceof Error && error.message === 'Already accepted by another rider') {
                    alert('This delivery was just accepted by another rider.');
                  } else if (error instanceof Error && error.message === 'This delivery is no longer available') {
                    alert('This delivery is no longer available.');
                  } else {
                    alert('Failed to accept job. Please try again.');
                  }
                } finally {
                  setIsAcceptingJob(false);
                }
              }}
              onReject={() => {
                navigate('home');
              }}
              onOpenChat={() => navigate('chat', currentJob.id)}
            />
          )}
          
          {currentScreen === 'navigation' && currentJob && (
            <ActiveJobScreen
              job={currentJob}
              riderId={userId}
              autoStartNav={navAutoStart}
              onBack={() => {
                setNavAutoStart(false);
                navigate('home');
              }}
              onArrivedAtPickup={() => {
                setJobStatus('at-pickup');
                persistActiveDelivery(currentJob.id, 'at-pickup');
                navigate('pickup-confirmation');
                notificationService.trigger(
                  'system',
                  '📍 Arrived at Pickup',
                  `You are at ${currentJob.pickupName}. Take photos to confirm collection.`,
                  { jobId: currentJob.id }
                );
                void dbService.markArrivedAtPickup(currentJob.id).catch((e) => {
                  console.error('Failed to sync arrived at pickup:', e);
                });
              }}
              onOpenPickupConfirmation={() => {
                setJobStatus('at-pickup');
                persistActiveDelivery(currentJob.id, 'at-pickup');
                navigate('pickup-confirmation');
                void dbService.markArrivedAtPickup(currentJob.id).catch((e) => {
                  console.error('Failed to sync arrived at pickup:', e);
                });
              }}
              onArrivedAtDelivery={() => {
                if (!isPickupConfirmed(currentJob.id)) {
                  setJobStatus('at-pickup');
                  persistActiveDelivery(currentJob.id, 'at-pickup');
                  navigate('pickup-confirmation');
                  notificationService.trigger(
                    'system',
                    '📦 Confirm Pickup First',
                    'Take pickup photos before heading to delivery confirmation.',
                    { jobId: currentJob.id }
                  );
                  return;
                }
                setJobStatus('at-delivery');
                persistActiveDelivery(currentJob.id, 'at-delivery');
                navigate('delivery-confirmation');
                notificationService.trigger(
                  'system',
                  '📍 Arrived at Delivery',
                  `You are at ${currentJob.deliveryName}. Confirm delivery with the customer.`,
                  { jobId: currentJob.id }
                );
                void dbService.markArrivedAtDelivery(currentJob.id).catch((e) => {
                  console.error('Failed to sync arrived at delivery:', e);
                });
              }}
              onOpenChat={() => navigate('chat', currentJob.id)}
            />
          )}
          
          {currentScreen === 'pickup-confirmation' && currentJob && (
            <PickupConfirmationScreen
              jobId={currentJob.id}
              riderId={userId}
              pickupAddress={currentJob.pickupAddress}
              onBack={() => navigate('navigation')}
              onConfirm={async (data) => {
                console.log('Pickup confirmed:', data);
                try {
                  const photoUrls: string[] = [];
                  let uploadFailed = false;
                  for (let i = 0; i < (data.photos?.length || 0); i++) {
                    try {
                      const compressed = await compressDataUrl(data.photos[i]);
                      const file = dataUrlToFile(compressed, `pickup-${i}.jpg`);
                      const url = await storageService.uploadDeliveryPhoto(
                        currentJob.id,
                        'pickup',
                        file
                      );
                      photoUrls.push(url);
                    } catch (uploadErr) {
                      uploadFailed = true;
                      console.error('Pickup photo upload failed:', uploadErr);
                    }
                  }

                  // Never block the job on Storage — rider must advance to delivery nav
                  await dbService.markJobPickedUp(currentJob.id);
                  await dbService.markEnRouteToDelivery(currentJob.id);
                  if (photoUrls.length) {
                    await dbService.savePickupProof(currentJob.id, {
                      photoUrls,
                      notes: data.notes,
                    });
                  } else if (data.notes || uploadFailed) {
                    await dbService.savePickupProof(currentJob.id, {
                      photoUrls: [],
                      notes:
                        (data.notes || '') +
                        (uploadFailed ? ' [photos pending retry — upload failed]' : ''),
                    }).catch(() => undefined);
                  }

                  if (uploadFailed && photoUrls.length === 0) {
                    alert(
                      'Pickup confirmed, but photos failed to upload. Continue to delivery — retry photos later if needed.'
                    );
                  }

                  notificationService.trigger(
                    'system',
                    '✅ Pickup Confirmed',
                    `Package collected from ${currentJob.pickupName}. Now heading to delivery.`,
                    { jobId: currentJob.id }
                  );
                  markPickupConfirmed(currentJob.id);
                  setJobStatus('going-to-delivery');
                  persistActiveDelivery(currentJob.id, 'going-to-delivery');
                  navigate('navigation');
                } catch (e) {
                  console.error('Failed to sync pickup status:', e);
                  alert('Failed to confirm pickup. Check your connection and try again.');
                }
              }}
            />
          )}
          
          {currentScreen === 'delivery-confirmation' && currentJob && (
            <DeliveryConfirmationScreen
              jobId={currentJob.id}
              deliveryAddress={currentJob.deliveryAddress}
              estimatedEarnings={currentJob.estimatedEarnings}
              isCOD={currentJob.isCOD}
              codAmount={currentJob.codAmount}
              requiresPin={currentJob.requiresPin}
              customerName={currentJob.customerName}
              riderId={userId}
              onBack={() => navigate('navigation')}
              onConfirm={async (data) => {
                console.log('Delivery confirmed:', data);
                try {
                  const riderProfile = await dbService.getRiderProfile(userId);
                  const vehicleType = riderProfile?.vehicleType || 'leg';

                  const riderEarning =
                    selectedJob?.earnings ||
                    dbService.resolveJobEarnings(selectedJob || {}, vehicleType);
                  const parsedEstimate = parseFloat(currentJob.estimatedEarnings.replace(/[₦,]/g, ''));
                  const shareRate = getRiderShareRate(vehicleType);
                  const deliveryFee =
                    parsedEstimate > 0
                      ? parsedEstimate
                      : riderEarning > 0 && shareRate > 0
                        ? Math.round(riderEarning / shareRate)
                        : 0;
                  const platformFee = Math.max(0, deliveryFee - riderEarning);
                  const companyFromDelivery = deliveryFee - riderEarning;
                  const companyTotal = companyFromDelivery + platformFee;
                  const tip = data.tip ? parseFloat(data.tip.replace(/[₦,]/g, '')) : 0;
                  const cashCollected = data.cashCollected
                    ? parseFloat(data.cashCollected.replace(/[₦,]/g, ''))
                    : undefined;

                  const deliveryPhotoUrls: string[] = [];
                  for (let i = 0; i < (data.photos?.length || 0); i++) {
                    try {
                      const compressed = await compressDataUrl(data.photos[i]);
                      const file = dataUrlToFile(compressed, `delivery-${i}.jpg`);
                      const url = await storageService.uploadDeliveryPhoto(
                        currentJob.id,
                        'delivery',
                        file
                      );
                      deliveryPhotoUrls.push(url);
                    } catch (uploadErr) {
                      console.error('Delivery photo upload failed:', uploadErr);
                    }
                  }

                  let signatureURL: string | undefined;
                  if (data.signature) {
                    try {
                      const compressedSig = await compressDataUrl(data.signature);
                      const sigFile = dataUrlToFile(compressedSig, 'signature.jpg');
                      signatureURL = await storageService.uploadDeliveryPhoto(
                        currentJob.id,
                        'delivery',
                        sigFile
                      );
                    } catch (sigErr) {
                      console.error('Signature upload failed:', sigErr);
                    }
                  }

                  const paymentBreakdown = {
                    deliveryFee,
                    platformFee,
                    riderEarning,
                    companyFromDelivery,
                    companyTotal,
                    vehicleType,
                    tip,
                    cashCollected: cashCollected ?? 0,
                    totalRiderEarning: riderEarning + tip,
                    completedAt: new Date().toISOString(),
                    riderId: userId,
                  };

                  await stopNativeNavigation();
                  await dbService.saveDeliveryProof(currentJob.id, {
                    photoUrls: deliveryPhotoUrls,
                    signatureURL,
                    cashCollected,
                    tip: tip > 0 ? tip : undefined,
                  });
                  await dbService.completeJob(currentJob.id, {
                    riderId: userId,
                    completedAt: new Date().toISOString(),
                    deliveryPhotoURL: deliveryPhotoUrls[0] || null,
                    deliveryPhotos: deliveryPhotoUrls,
                    proofPhoto: deliveryPhotoUrls[0] || null,
                    signatureURL: signatureURL || null,
                    cashCollected: cashCollected ?? null,
                    codCollected: data.cashCollected || null,
                    tip: tip > 0 ? tip : null,
                  });

                  clearActiveDelivery();
                  setActiveDeliveryJobId(null);
                  setSelectedJob(null);
                  setNavAutoStart(false);
                  await dbService.clearRiderActiveJob(userId);
                  await dbService.updateOrderPaymentBreakdown(currentJob.id, paymentBreakdown);

                  try {
                    await walletService.addEarning(
                      userId,
                      riderEarning,
                      'delivery',
                      `Delivery #${currentJob.id} (${vehicleType} payout)`,
                      currentJob.id
                    );
                    if (tip > 0) {
                      await walletService.addEarning(
                        userId,
                        tip,
                        'tip',
                        `Customer tip - Job #${currentJob.id}`,
                        currentJob.id
                      );
                    }
                    await dbService.updateRiderStats(userId, vehicleType, riderEarning);
                  } catch (paymentError) {
                    console.error('Payment processing failed after delivery:', paymentError);
                  }

                  const totalEarned = riderEarning + tip;
                  notificationService.trigger(
                    'earnings-update',
                    '🎉 Delivery Complete!',
                    tip > 0
                      ? `You earned ₦${riderEarning.toLocaleString()} + ₦${tip.toLocaleString()} tip = ₦${totalEarned.toLocaleString()} total!`
                      : `You earned ₦${riderEarning.toLocaleString()} for this delivery. Keep it up!`,
                    { jobId: currentJob.id, amount: `₦${totalEarned.toLocaleString()}` }
                  );

                  navigate('earnings');
                } catch (error) {
                  console.error('Failed to complete delivery:', error);
                  alert('Failed to complete delivery. Check your connection and try again.');
                }
              }}
            />
          )}
          
          {currentScreen === 'earnings' && (
            <EarningsScreen 
              onBack={() => navigate('home')}
              riderId={userId}
            />
          )}
          
          {currentScreen === 'shift-management' && (
            <ShiftManagementScreen onBack={() => navigate('home')} />
          )}
          
          {currentScreen === 'profile' && (
            <ProfileScreen 
              onBack={() => navigate('home')}
              onNavigateToSettings={() => navigate('settings')}
              onNavigateToFleetTypeSelection={() => navigate('fleet-type-selection')}
              onNavigateToEditProfile={() => navigate('edit-profile')}
              onNavigateToGearCompliance={() => navigate('gear-compliance')}
              riderFleetType={riderFleetType}
            />
          )}
          
          {currentScreen === 'edit-profile' && (
            <EditProfileScreen 
              onBack={() => navigate('profile')} 
              onSave={(data) => {
                console.log('Profile data saved:', data);
              }}
            />
          )}
          
          {currentScreen === 'settings' && (
            <SettingsScreen onBack={() => navigate('profile')} />
          )}
          
          {currentScreen === 'incident-report' && (
            <IncidentReportScreen onBack={() => navigate('home')} />
          )}
          
          {currentScreen === 'history' && (
            <HistoryScreen onBack={() => navigate('home')} />
          )}
          
          {currentScreen === 'design-system' && (
            <DesignSystemScreen 
              onNavigate={(screen) => navigate(screen as Screen)}
            />
          )}
          
          {currentScreen === 'fleet-type-selection' && (
            <FleetTypeSelectionScreen
              onBack={() => navigate('profile')}
              currentFleetType={riderFleetType}
              onSelect={async (fleetType) => {
                setRiderFleetType(fleetType);
                // Update vehicleType in rider profile
                try {
                  const vehicleType: 'bike' | 'bicycle' | 'leg' = 
                    fleetType === 'rider' ? 'bike' : 
                    fleetType === 'leg-fleet' ? 'leg' : 
                    'bicycle';
                  await dbService.updateRiderProfile(userId, { 
                    fleetType,
                    vehicleType 
                  });
                  console.log(`✅ Updated vehicle type to: ${vehicleType}`);
                } catch (error) {
                  console.error('Failed to update vehicle type:', error);
                }
              }}
            />
          )}
          
          {currentScreen === 'notifications' && (
            <NotificationScreen 
              onBack={() => navigate('home')}
              onNavigate={(screen, data) => navigate(screen as Screen, data)}
            />
          )}
          
          {currentScreen === 'chat' && currentJob && (
            <CustomerChat 
              customerName={currentJob.customerName}
              customerPhone={currentJob.customerPhone}
              jobId={currentJob.id}
              deliveryAddress={currentJob.deliveryAddress}
              riderName={riderName}
              onBack={() => {
                if (activeDeliveryJobId || selectedJob?.id) {
                  navigate('navigation', activeDeliveryJobId || selectedJob?.id || undefined);
                } else {
                  navigate('home');
                }
              }}
              onCall={() => {
                window.location.href = `tel:${currentJob.customerPhone}`;
              }}
            />
          )}
          
          {currentScreen === 'application-review' && (
            <ApplicationReviewScreen onComplete={() => navigate('training')} />
          )}
          
          {currentScreen === 'training' && (
            <TrainingScreen onComplete={() => navigate('home')} />
          )}
          
          {currentScreen === 'performance' && (
            <PerformanceDashboardScreen onBack={() => navigate('home')} />
          )}
          
          {currentScreen === 'help-center' && (
            <HelpCenterScreen 
              onBack={() => navigate('home')} 
              onStartChat={() => {
                const jobId = activeDeliveryJobId || selectedJob?.id;
                if (jobId) {
                  navigate('chat', jobId);
                } else {
                  alert('Accept or resume a delivery first to chat with the customer.');
                }
              }}
            />
          )}
          
          {currentScreen === 'promotions' && (
            <PromotionsScreen onBack={() => navigate('home')} />
          )}
          
          {currentScreen === 'leaderboard' && (
            <LeaderboardScreen onBack={() => navigate('home')} />
          )}
          
          {currentScreen === 'advanced-settings' && (
            <AdvancedSettingsScreen onBack={() => navigate('settings')} />
          )}
          
          {currentScreen === 'vehicle-management' && (
            <VehicleManagementScreen onBack={() => navigate('settings')} />
          )}
          
          {currentScreen === 'gear-compliance' && (
            <GearComplianceScreen 
              onBack={() => navigate('settings')} 
              onComplete={() => navigate('home')}
            />
          )}
          
          {currentScreen === 'sos-demo' && (
            <SOSButton onEmergency={() => {
              console.log('EMERGENCY ACTIVATED!');
              // In production, trigger emergency services
            }} />
          )}
        </div>

        {/* SOS Button - Always visible on active screens */}
        {(currentScreen === 'home') && (
          <SOSButton onEmergency={() => {
            console.log('EMERGENCY ACTIVATED!');
          }} />
        )}
        {currentScreen === 'navigation' && (
          <SOSButton
            placement="navigation"
            onEmergency={() => {
              console.log('EMERGENCY ACTIVATED!');
            }}
          />
        )}
      </div>
    </div>
  );
}